import { OptionalTimeZone } from '../../timezones'
import {
  ExtendedTemplateItemOptions,
  FilenameTemplateItem,
} from './FilenameTemplateItem'

type ConcreteTestSet = {
  placementDate?: Date
  createdDate?: Date
  testSetId?: string
  concreteTests: { fieldNumber?: string; testSubId?: string }[]
  concretePlacements: { placementId?: string }[]
}

export class ConcreteTestSetFilenameTemplateItem<
  Property extends keyof ConcreteTestSet,
  ID extends string
> extends FilenameTemplateItem<
  ConcreteTestSet,
  'ConcreteTestSet',
  Property,
  ID
> {
  value<T extends ConcreteTestSet>(
    record: T | undefined,
    tz: OptionalTimeZone
  ): string {
    if (this.property === 'testSetId')
      return this.resolve(record?.['testSetId'])
    return this.resolveDate(
      record?.['placementDate'] ?? record?.['createdDate'],
      tz
    )
  }
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcreteTestSet, Property, ID>
  ) {
    super({ classType: 'ConcreteTestSet', ...options })
  }
}

type ConcreteTestSetWithRelations = Pick<
  ConcreteTestSet,
  'concretePlacements' | 'concreteTests'
>
export class ConcreteTestSetRelationFilenameTemplateItem<
  Property extends keyof ConcreteTestSetWithRelations,
  SubProperty extends keyof ConcreteTestSetWithRelations[Property][number],
  ID extends string
> extends ConcreteTestSetFilenameTemplateItem<Property, ID> {
  /**
   * Filenames that use this extended concrete test set template item need to
   * access a sub property of one of the concrete test's properties
   */
  subProperty: SubProperty
  value<T extends ConcreteTestSet>(record: T | undefined): string {
    const values =
      this.property === 'concretePlacements'
        ? (record?.concretePlacements ?? []).reduce(
            (values, { placementId }) => {
              if (placementId) values.push(placementId)
              return values
            },
            Array<string>()
          )
        : (record?.concreteTests ?? []).reduce((values, test) => {
            const value = test[this.subProperty as keyof typeof test]
            if (value) values.push(value)
            return values
          }, Array<string>())
    if (!values.length) return this.resolve(undefined)
    return this.resolve(values.sort().join('_'))
  }
  /**
   * @see {@link ConcreteTestSetFilenameTemplateItem}
   */
  constructor({
    subProperty,
    ...options
  }: ExtendedTemplateItemOptions<ConcreteTestSet, Property, ID> & {
    subProperty: SubProperty
  }) {
    super(options)
    this.subProperty = subProperty
  }
}

type ConcreteTest = { testSubId?: string; fieldNumber?: string }
export class ConcreteTestFilenameTemplateItem<
  Property extends keyof ConcreteTest,
  ID extends string
> extends FilenameTemplateItem<ConcreteTest, 'ConcreteTest', Property, ID> {
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcreteTest, Property, ID>
  ) {
    super({ classType: 'ConcreteTest', ...options })
  }
}

type ConcreteMixDesign = {
  mixDesignNumber?: string
  clientDesignNumber?: string
  class?: string
}
export class ConcreteMixDesignFilenameTemplateItem<
  Property extends keyof ConcreteMixDesign,
  ID extends string
> extends FilenameTemplateItem<
  ConcreteMixDesign,
  'ConcreteMixDesign',
  Property,
  ID
> {
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcreteMixDesign, Property, ID>
  ) {
    super({ classType: 'ConcreteMixDesign', ...options })
  }
}

type ConcreteMaterial = { placementType?: string; shortName?: string }
export class ConcreteMaterialFilenameTemplateItem<
  Property extends keyof ConcreteMaterial,
  ID extends string
> extends FilenameTemplateItem<
  ConcreteMaterial,
  'ConcreteMaterial',
  Property,
  ID
> {
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcreteMaterial, Property, ID>
  ) {
    super({ classType: 'ConcreteMaterial', ...options })
  }
}

type ConcretePlacement = { placementId?: string }
export class ConcretePlacementFilenameTemplateItem<
  Property extends keyof ConcretePlacement,
  ID extends string
> extends FilenameTemplateItem<
  ConcretePlacement,
  'ConcretePlacement',
  Property,
  ID
> {
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcretePlacement, Property, ID>
  ) {
    super({ classType: 'ConcretePlacement', ...options })
  }
}
